/**
 * Which inventory this browser is currently viewing: a per-device choice, kept
 * in localStorage for the same reason capture's own stopwatch is
 * (docs/notes/decisions.md) -- it is one device's preference, not a fact any
 * other screen or device has to agree with, and the server re-checks it
 * against `memberships` on every request regardless of what this says. A
 * private window or blocked storage just means every request defaults to the
 * account's own inventory, which is the right fallback anyway.
 *
 * The same id is mirrored into a cookie, because `lib/api.ts` can send a
 * header and an `<img src>` cannot: without the cookie, every photo in an
 * inventory other than the account's home one loaded as a broken image. The
 * worker reads the header first and the cookie second, and checks either
 * against `memberships` the same way.
 */

import { INVENTORY_COOKIE } from "@iltocat/shared";

const KEY = "iltocat.activeInventory";
const ACCOUNT_KEY = "iltocat.activeAccount";

export function getActiveInventory(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/**
 * `Path=/api` so it only rides on requests to the worker, `SameSite=Strict` so
 * no other site can make a request carry it, and not `HttpOnly` because this
 * script is what writes it. It is a hint, not a credential: the session cookie
 * is still what says who is asking.
 */
function writeCookie(id: string | null): void {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = id
      ? `${INVENTORY_COOKIE}=${encodeURIComponent(id)}; Path=/api; Max-Age=31536000; SameSite=Strict${secure}`
      : `${INVENTORY_COOKIE}=; Path=/api; Max-Age=0; SameSite=Strict${secure}`;
  } catch {
    // No document, or cookies refused: requests still carry the header, and
    // only an <img> falls back to the home inventory -- never worth failing a switch over.
  }
}

/**
 * Brings the cookie into line with localStorage, once at boot. A browser that
 * switched before the cookie existed has only the localStorage half, and its
 * photos would stay broken until the next switch.
 */
export function syncInventoryCookie(): void {
  writeCookie(getActiveInventory());
}

/**
 * Drops this device's inventory choice when the signed-in account has changed
 * since its last visit -- a fresh magic link followed while another account's
 * session was still active leaves this choice naming an inventory the new
 * account has no `memberships` row for, which the server correctly refuses
 * with the same 404 an id that never existed gets
 * (docs/notes/invariants.md), breaking every screen until something clears
 * it. Returns whether the account actually changed, so the caller can also
 * drop the offline queue -- the other per-device, not per-account, state a
 * fresh sign-in this way bypasses `useSession.ts`'s `signOut()` for.
 */
export function reconcileAccount(accountId: string): boolean {
  let previous: string | null;
  try {
    previous = window.localStorage.getItem(ACCOUNT_KEY);
    window.localStorage.setItem(ACCOUNT_KEY, accountId);
  } catch {
    return false;
  }
  if (previous === null || previous === accountId) return false;

  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing to fall back to: see the try/catch this mirrors above.
  }
  writeCookie(null);
  return true;
}

/** Switches the device to another inventory and reloads -- everything on screen (items, taxonomy, members) belongs to the one being left. */
export function switchInventory(id: string): void {
  try {
    window.localStorage.setItem(KEY, id);
  } catch {
    // Nothing to fall back to: the switch just will not stick past this load.
  }
  writeCookie(id);
  window.location.href = "/";
}
